import { IFilter, IFinancialReportFilter } from 'props';
import { createContext, useContext, ReactNode, useMemo, useCallback, useEffect } from 'react';
import {
  requestCollaboratorsReport,
  requestDemandsReport,
  requestPaymentsReport,
  requestSolicitationsReport,
} from '../controller/ReportController';
import usePersistedState from '../hooks/usePersistedState';
import { useAlert } from '../hooks/useAlert';
import { useAuth } from './auth';

type ProviderProps = {
  children: ReactNode;
};

type ReportContextProps = {
  isGeneratingReport: boolean;
  setIsGeneratingReport: React.Dispatch<React.SetStateAction<boolean>>;
  handleGenerateSolicitationReport: (input: IFilter) => void;
  handleGenerateDemandReport: (input: IFilter) => void;
  handleGenerateCollaboratorReport: (userId: string) => void;
  handleGenerateFinancialReport: (input: IFinancialReportFilter) => void;
};

const ReportContext = createContext<ReportContextProps>({} as ReportContextProps);

export function ReportProvider({ children }: ProviderProps) {
  const TIMER_TO_RESET_GENERATING_REPORT_IN_MS = 300000;
  const alert = useAlert();
  const { tokenIsValid } = useAuth();
  const [isGeneratingReport, setIsGeneratingReport] = usePersistedState(
    'isGeneratingReport',
    false,
    false,
  );

  const handleGenerateSolicitationReport = useCallback(
    (input: IFilter) => {
      if (isGeneratingReport) return;

      setIsGeneratingReport(true);
      requestSolicitationsReport(input)
        .then(() => {
          alert.show({
            message: 'Relatório solicitado com sucesso. Você receberá uma notificação quando estiver pronto.',
            type: 'success'
          });
          setIsGeneratingReport(false);
        })
        .catch(() => {
          alert.show({ message: 'Erro ao solicitar geração de relatório de solicitações', type: 'error' });
          setIsGeneratingReport(false);
        });
    },
    [alert, isGeneratingReport, setIsGeneratingReport],
  );

  const handleGenerateDemandReport = useCallback(
    (input: IFilter) => {
      if (isGeneratingReport) return;

      setIsGeneratingReport(true);
      requestDemandsReport(input)
        .then(() => {
          alert.show({ message: 'Relatório de demandas solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
        })
        .catch(() => {
          alert.show({ message: 'Erro ao solicitar relatório de demandas', type: 'error' });
        })
        .finally(() => {
          setIsGeneratingReport(false);
        });
    },
    [alert, isGeneratingReport, setIsGeneratingReport],
  );

  const handleGenerateCollaboratorReport = useCallback(
    (userId: string) => {
      if (isGeneratingReport) return;

      setIsGeneratingReport(true);
      requestCollaboratorsReport(userId)
        .then(() => {
          alert.show({ message: 'Relatório de colaboradores solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
        })
        .catch(() => {
          alert.show({ message: 'Erro ao solicitar relatório de colaboradores', type: 'error' });
        })
        .finally(() => {
          setIsGeneratingReport(false);
        });
    },
    [alert, isGeneratingReport, setIsGeneratingReport],
  );

  const handleGenerateFinancialReport = useCallback(
    (input: IFinancialReportFilter) => {
      if (isGeneratingReport) return;

      setIsGeneratingReport(true);
      requestPaymentsReport(input)
        .then(() => {
          alert.show({ message: 'Relatório de pagamentos solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
        })
        .catch(() => {
          alert.show({ message: 'Erro ao solicitar relatório de pagamentos', type: 'error' });
        })
        .finally(() => {
          setIsGeneratingReport(false);
        });
    },
    [alert, isGeneratingReport, setIsGeneratingReport],
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setIsGeneratingReport(false);
    }
  }, [tokenIsValid, setIsGeneratingReport]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsGeneratingReport(false);
    }, TIMER_TO_RESET_GENERATING_REPORT_IN_MS);

    return () => clearTimeout(timer);
  }, [setIsGeneratingReport]);

  const contextValue = useMemo(
    () => ({
      isGeneratingReport,
      setIsGeneratingReport,
      handleGenerateSolicitationReport,
      handleGenerateDemandReport,
      handleGenerateCollaboratorReport,
      handleGenerateFinancialReport,
    }),
    [
      handleGenerateCollaboratorReport,
      handleGenerateDemandReport,
      handleGenerateFinancialReport,
      handleGenerateSolicitationReport,
      isGeneratingReport,
      setIsGeneratingReport,
    ],
  );

  return <ReportContext.Provider value={contextValue}>{children}</ReportContext.Provider>;
}

export const useReport: () => ReportContextProps = () => useContext(ReportContext);
